import {
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ExpandMore, HelpOutline } from '@mui/icons-material';
import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import useExternalTriggerCurl, { getPayloadExamples } from './getSamplePayload';
import CopyButton from 'js/components/data_sources/add_ds_form/IncomingWebhookConfiguration/CopyButton';
import { Tabs } from 'new/elements/Tabs/Tabs';
import styles from './ExternalTriggerConfig.module.scss';

type ExternalTriggerConfigProps = {
  data?: any;
};

export default function ExternalTriggerConfig({ data }: ExternalTriggerConfigProps) {
  const [activeTab, setActiveTab] = useState('combined');
  const [expandedContext, setExpandedContext] = useState(false);

  const flowNodeId = data?.flow_node_id;
  const examples = getPayloadExamples();

  const displayPayload = useExternalTriggerCurl(
    flowNodeId,
    true,
    examples[activeTab as keyof typeof examples].payloadType,
  );
  const copyPayload = useExternalTriggerCurl(
    flowNodeId,
    false,
    examples[activeTab as keyof typeof examples].payloadType,
  );

  const tabs = Object.entries(examples).map(([key, example]) => ({
    value: key,
    label: example.title,
  }));

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };
  const handleContextToggle = () => {
    setExpandedContext(!expandedContext);
  };

  return (
    <Stack gap="16px">
      <Stack gap="8px" className={styles.externalTriggerConfig}>
        <Typography variant="subtitle2">API Instructions & Examples</Typography>
        <Typography variant="bodyDefault">
          Use these ready-to-test examples to trigger your flow dynamically with custom overrides.
          Simply copy any example below, update the values to match your needs, and test
          immediately.
        </Typography>

        {/* Expandable Context */}
        <Accordion
          expanded={expandedContext}
          onChange={handleContextToggle}
          className={styles.accordionItem}
        >
          <AccordionSummary
            expandIcon={<ExpandMore className={styles.accordionSummaryIcon} />}
            className={styles.accordionSummary}
          >
            <Box className={styles.accordionTitle}>
              <HelpOutline className={styles.accordionTitleIcon} sx={{ mr: 1 }} />

              <Typography variant="subtitle2" className={styles.accordionTitleText}>
                What are External Triggers?
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails className={styles.accordionDetails}>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
              External triggers let you start this flow programmatically via API calls and override
              settings for each execution.
              <br />
              How it works:
            </Typography>
            <List dense>
              <ListItem sx={{ py: 0, px: 0 }}>
                <ListItemText primary="• Your flow gets a unique API endpoint (shown below)" />
              </ListItem>
              <ListItem sx={{ py: 0, px: 0 }}>
                <ListItemText primary="• Make a request to start the flow with custom settings" />
              </ListItem>
              <ListItem sx={{ py: 0, px: 0 }}>
                <ListItemText primary="• Override sources, destinations, or global variables per run" />
              </ListItem>
              <ListItem sx={{ py: 0, px: 0 }}>
                <ListItemText primary="• Perfect for automation, A/B testing, and dynamic workflows" />
              </ListItem>
            </List>
            <Typography variant="subtitle2">Payload: Sections</Typography>
            <table className={styles.docTable}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Type</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>name</code>
                  </td>
                  <td>string</td>
                  <td>Required. User-defined name of the task.</td>
                </tr>
                <tr>
                  <td>
                    <code>global</code>
                  </td>
                  <td>object</td>
                  <td>Optional. Global settings for the task.</td>
                </tr>
                <tr>
                  <td>
                    <code>sources</code>
                  </td>
                  <td>array</td>
                  <td>Optional. List of source definitions.</td>
                </tr>
                <tr>
                  <td>
                    <code>sinks</code>
                  </td>
                  <td>array</td>
                  <td>Optional. List of sink definitions.</td>
                </tr>
              </tbody>
            </table>
            <br />
            <Typography variant="subtitle2">Payload:Fields & Properties</Typography>
            <Typography variant="bodyDefault">
              The <code>global</code>, <code>sources[]</code>, and <code>sinks[]</code> sections all
              share the following optional fields:
            </Typography>
            <table className={styles.docTable}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Type</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>properties</code>
                  </td>
                  <td>object</td>
                  <td>Key-value pairs appended to the resource config (overriding duplicates).</td>
                </tr>
                <tr>
                  <td>
                    <code>variables</code>
                  </td>
                  <td>object</td>
                  <td>Key-value pairs used for variable substitution.</td>
                </tr>
                <tr>
                  <td>
                    <code>data_credentials_id</code>
                  </td>
                  <td>number</td>
                  <td>ID referencing the credentials for this section.</td>
                </tr>
                <tr>
                  <td>
                    <code>config</code>
                  </td>
                  <td>object</td>
                  <td>Custom resource config that fully replaces the default config.</td>
                </tr>
                <tr>
                  <td>
                    <code>id</code> (for <code>sources</code> / <code>sinks</code>)
                  </td>
                  <td>number</td>
                  <td>
                    Required in <code>sources</code> and <code>sinks</code>. Identifies the specific
                    resource.
                  </td>
                </tr>
              </tbody>
            </table>
          </AccordionDetails>
        </Accordion>
        <Tabs tabs={tabs} value={activeTab} onChange={handleTabChange} />
        <Stack gap="12px">
          <div className={styles.tabDescription}>
            <Typography variant="subtitle2">
              {examples[activeTab as keyof typeof examples].title}
            </Typography>
            <Typography variant="bodyDefault">
              {examples[activeTab as keyof typeof examples].description}
            </Typography>
          </div>
          <div className={styles.codeContainer}>
            <SyntaxHighlighter language="bash" style={a11yDark}>
              {displayPayload}
            </SyntaxHighlighter>
            <Stack justifyContent="flex-end" direction="row" alignItems="center" gap={2}>
              <CopyButton title="Copy cURL" toCopy={copyPayload} />
            </Stack>
          </div>
          <div className={styles.usageNotes}>
            <Typography variant="subtitle2" sx={{ marginBottom: 'var(--spacing-md)' }}>
              Usage Notes:
            </Typography>
            <Stack gap="4px">
              <Typography variant="bodySmall">
                <strong>• Customize values:</strong> Replace example variables with your actual
                requirements
              </Typography>
              {activeTab === 'sources' && (
                <Typography variant="bodySmall">
                  • <strong>Match your IDs:</strong> Ensure Source IDs match your configured
                  connections
                </Typography>
              )}
              {activeTab === 'sinks' && (
                <Typography variant="bodySmall">
                  • <strong>Match your IDs:</strong> Ensure Sink IDs match your configured
                  connections
                </Typography>
              )}
              {activeTab === 'combined' && (
                <Typography variant="bodySmall">
                  • <strong>Match your IDs:</strong> Ensure Source IDs and Sink IDs match your
                  configured connections
                </Typography>
              )}
              {(activeTab === 'sources' || activeTab === 'sinks' || activeTab === 'combined') && (
                <Typography variant="bodySmall">
                  • <strong>Check credentials:</strong> Credential IDs must match those in your
                  Credentials Group
                </Typography>
              )}
            </Stack>
          </div>
        </Stack>
      </Stack>
    </Stack>
  );
}
